import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Broadcast } from "@phosphor-icons/react";
import { useCountry } from "../hooks/useCountry";
import { useLiveOverride } from "../hooks/useLiveOverride";
import { getStreamInfoForTz } from "../utils/streamTime";

// Compact strip + sticky countdown. Auto-refreshes every minute.
// Reacts to the owner-set live override:
//   - "offline" → skips today's slot, shows tomorrow's next stream
//   - "live"    → shows "Live now" CTA instead of a countdown
//   - "auto"    → normal next-stream behaviour
export const StreamTimeBanner = ({ variant = "card" }) => {
    const { country } = useCountry();
    const override = useLiveOverride();
    const skipToday = override === "offline";
    const forcedLive = override === "live";

    const [info, setInfo] = useState(() =>
        country ? getStreamInfoForTz(country.tz, new Date(), { skipToday }) : null
    );

    useEffect(() => {
        if (!country) {
            setInfo(null);
            return;
        }
        const update = () => setInfo(getStreamInfoForTz(country.tz, new Date(), { skipToday }));
        update();
        const id = setInterval(update, 60000);
        return () => clearInterval(id);
    }, [country, skipToday]);

    if (!country || !info) return null;

    if (variant === "compact") {
        return (
            <Link
                to="/live"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/70 hover:text-white hover:border-white/30 transition font-mono"
                data-testid="stream-time-compact"
            >
                <span className="leading-none">{country.flag}</span>
                <span>{info.dayLabel} · {info.timeLabel}</span>
                <span className="text-white/35">·</span>
                <span className="text-brand-cyan">{forcedLive ? "live now" : info.countdown}</span>
            </Link>
        );
    }

    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={forcedLive ? "live" : skipToday ? "off" : "auto"}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.4 }}
                className={`relative rounded-2xl border backdrop-blur-md p-5 flex items-center gap-5 flex-wrap ${
                    forcedLive
                        ? "border-red-500/40 bg-gradient-to-r from-red-500/[0.12] to-purple-500/[0.08]"
                        : "border-white/10 bg-gradient-to-r from-cyan-500/[0.07] to-purple-500/[0.07]"
                }`}
                data-testid="stream-time-banner"
                data-state={forcedLive ? "live" : skipToday ? "skip-today" : "auto"}
            >
                <div className="flex items-center gap-3 flex-1 min-w-[240px]">
                    <span className="text-3xl leading-none" data-testid="stream-time-flag">{country.flag}</span>
                    <div>
                        <div className={`eyebrow ${forcedLive ? "text-red-300/90" : "text-cyan-300/80"}`}>
                            {forcedLive ? "Live right now" : skipToday ? "No stream today · next one" : "Your next stream"}
                        </div>
                        <div className="font-display text-white text-xl mt-0.5" data-testid="stream-time-label">
                            {forcedLive ? "itz_ool is on Twitch" : `${info.dayLabel} · ${info.timeLabel}`}
                        </div>
                        <div className="text-xs text-white/45 font-mono mt-1">
                            {forcedLive ? "Pull up — chat is hot." : `${country.name} · ${info.tzShort}`}
                        </div>
                    </div>
                </div>

                {!forcedLive && (
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/40 border border-white/10" data-testid="stream-time-countdown">
                        <Clock size={14} className="text-brand-cyan" weight="bold" />
                        <span className="font-mono text-sm text-white">{info.countdown}</span>
                    </div>
                )}

                {forcedLive ? (
                    <a
                        href="https://twitch.tv/itz_ool"
                        target="_blank"
                        rel="noreferrer"
                        className="btn-sweep inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500 text-white text-sm font-semibold hover:bg-red-400 transition"
                        data-testid="stream-time-watch-now-btn"
                    >
                        <Broadcast size={13} weight="fill" /> Watch on Twitch
                    </a>
                ) : (
                    <Link
                        to="/live"
                        className="btn-sweep inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black text-sm font-semibold hover:bg-brand-cyan transition"
                        data-testid="stream-time-watch-btn"
                    >
                        Set reminder <ArrowRight size={13} weight="bold" />
                    </Link>
                )}
            </motion.div>
        </AnimatePresence>
    );
};
